/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useRef, useState, useEffect, type ComponentProps } from 'react';
import useKeys, { type KeyHandlers } from 'effects/keys';
import './Submenu.scss';

const DEFAULT_ITEMS: SubmenuItem[] = [];
const DEFAULT_ON_CLOSE = () => {};

const letters = 'abcdefghijklmnopqrstuvwxyzæøå'.split('');

interface SubmenuItem extends Omit<ComponentProps<'a'>, 'title' | 'key'> {
  key: string | number;
  title: string;
}

interface SubmenuProps {
  id?: string;
  items?: SubmenuItem[];
  open?: boolean;
  onClose?: () => void;
}

/**
 * This is the Submenu component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function Submenu({
  id = 'sub',
  items = DEFAULT_ITEMS,
  open = false,
  onClose = DEFAULT_ON_CLOSE,
}: SubmenuProps) {
  const content = useRef<HTMLUListElement>(null);
  const [current, setCurrent] = useState(0);
  const [height, setHeight] = useState(0);
  const [maxHeight, setMaxHeight] = useState(0);
  const [opened, setOpened] = useState(false);
  const letterKeys: KeyHandlers = {};

  function onLetter({ key }: KeyboardEvent) {
    const startsWithKey = ({ title }: { title: string }) =>
      title[0].toLowerCase() === key;

    // useKeys keeps the handlers from the first render, so `current` is read
    // from the state update, as for up and down.
    setCurrent(prev => {
      // The next one after the current, then wrap around to the first.
      let index = items.findIndex((item, i) => i > prev && startsWithKey(item));

      if (index === -1) {
        index = items.findIndex(startsWithKey);
      }

      return index !== -1 ? index : prev;
    });
  }

  letters.forEach(letter => {
    letterKeys[letter] = onLetter;
  });

  useKeys('submenu', open, {
    esc: onClose,
    up: e => {
      e.preventDefault();
      setCurrent(prev => {
        const next = prev - 1;
        if (next < 0) {
          return items.length - 1;
        }

        return next;
      });
    },
    down: e => {
      e.preventDefault();
      setCurrent(prev => {
        const next = prev + 1;
        if (next >= items.length) {
          return 0;
        }

        return next;
      });
    },
    home: e => {
      e.preventDefault();
      setCurrent(0);
    },
    end: e => {
      e.preventDefault();
      setCurrent(items.length - 1);
    },
    ...letterKeys,
  });

  useEffect(() => {
    if (open && opened) {
      const item = content.current!.querySelector<HTMLElement>(
        `li:nth-child(${current + 1}) > a`
      );
      if (item) {
        item.focus();
      }
    }
  }, [opened, open, current]);

  useEffect(() => {
    if (!open) {
      setCurrent(0);
    }
  }, [open]);

  useEffect(() => {
    function onResize() {
      setMaxHeight(content.current!.offsetHeight);
    }

    window.addEventListener('resize', onResize);
    const timeout = setTimeout(onResize, 120);
    return function cleanup() {
      clearTimeout(timeout);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setHeight(maxHeight);
    } else {
      setHeight(0);
    }
  }, [open, maxHeight]);

  return (
    <div
      id={id}
      className="submenu"
      style={{ height }}
      onTransitionEnd={() => {
        setOpened(open);
      }}
    >
      <ul ref={content} role="none">
        {items.map(({ key, title, ...childProps }) => (
          <li key={key} role="none">
            <a
              {...childProps}
              tabIndex={open ? undefined : -1}
              data-event="Undermeny"
              data-label={title}
              dangerouslySetInnerHTML={{ __html: title }}
            />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default Submenu;
